/**
 * App-root call context. Hoists call state out of MessageArea so an
 * active call survives channel switches — Slack-Huddles-parity move.
 *
 * Before this provider, `useCallManagement` ran inside each MessageArea
 * instance. Switching channels flipped the multiplex `hidden` className
 * on the previous MessageArea, which cascaded to its `<InAppCallDialog>`
 * descendant. Net effect: a call started in #general would appear to
 * "end" the moment the user clicked over to #random, even though the
 * LiveKit session was still alive — confusing and not Huddles-like.
 *
 * Now `<InAppCallDialog>` lives at the App root (mounted in main.tsx
 * alongside `<SessionExpiredModal>`) and reads its state from this
 * provider. Channel-side call buttons call `useCall().joinCall(...)`
 * with explicit `channelId / workspaceId / channelName` instead of
 * relying on closure binding to the active MessageArea.
 *
 * `joiningCallChannelId` is exposed so per-channel call buttons can
 * show the spinner only on the channel whose call is being joined,
 * not every channel's button at once.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useZero } from "@rocicorp/zero/react";
import { useAuth } from "@workos-inc/authkit-react";
import { toast } from "sonner";
import { useCurrentUser } from "@/lib/current-user";
import {
  createChannelCallToken,
  warmCallMediaPermissions,
  type ChannelCallToken,
} from "@/lib/calls";
import { mutators } from "@/lib/mutators";
import { getAccessTokenWithRetry } from "@/lib/auth-token";
import { isElectronRuntime } from "@/lib/logout";

const CALLS_ENABLED = import.meta.env.VITE_FEATURE_CALLS !== "false";

interface ActiveCall {
  channelId: string;
  /** Null for DMs from Home view (no active workspace context). The
   *  server doesn't actually need this for DMs; tracking it here is
   *  for any UI that wants to show "in workspace X" affordances. */
  workspaceId: string | null;
  mode: "audio" | "video";
  serverUrl: string;
  token: string;
  joinUrl: string;
  channelName: string;
}

interface ElectronCallWindowPayload {
  serverUrl: string;
  token: string;
  joinUrl: string;
  mode: "audio" | "video";
  channelName: string;
}

export interface JoinCallArgs {
  channelId: string;
  /** Workspace channels MUST pass their workspace id; DMs from Home
   *  view (no active workspace) may pass null. The server treats it
   *  as a belt-and-suspenders check when present and skips it when
   *  absent — channel-member membership is the auth gate. */
  workspaceId: string | null;
  channelName: string;
  mode: "audio" | "video";
  /** Suppress the `started_*_call` system message (used by
   *  incoming-call accept where the start event came from the caller,
   *  not the joining user). */
  skipNotification?: boolean;
}

interface CallContextValue {
  callsEnabled: boolean;
  /** True for the channel currently in the join handshake (token
   *  fetch + media permissions). Buttons compare their own channelId
   *  to this to render a spinner only on the right channel. */
  joiningCallChannelId: string | null;
  joiningCallMode: "audio" | "video" | null;
  /** True when ANY call is mid-join or in progress. Channel-side call
   *  buttons disable themselves on this so the user can't fire two
   *  calls at once across the workspace. */
  isCallActionDisabled: boolean;
  joinCall: (args: JoinCallArgs) => Promise<void>;
  /** Imperative close — used by the dialog's leave button and the
   *  LiveKit `onDisconnected` callback. */
  endCall: () => void;
  handleInlineDisconnected: () => void;
  /** State the App-root `<InAppCallDialog>` reads. */
  activeCall: ActiveCall | null;
  callDialogOpen: boolean;
  nativeCallWindowOpen: boolean;
  popOutCall: () => Promise<boolean>;
  closeNativeCallWindow: () => Promise<boolean>;
}

const CallContext = createContext<CallContextValue | null>(null);

export function CallProvider({ children }: { children: ReactNode }) {
  const zero = useZero();
  const { user } = useCurrentUser();
  const { getAccessToken } = useAuth();

  const [joiningCallChannelId, setJoiningCallChannelId] = useState<
    string | null
  >(null);
  const [joiningCallMode, setJoiningCallMode] = useState<
    "audio" | "video" | null
  >(null);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [nativeCallWindowOpen, setNativeCallWindowOpen] = useState(false);

  // Tracks which (channelId, userId) the in-flight call belongs to so
  // `call_ended` always targets the channel where the call STARTED,
  // even if the user navigates away mid-call. Survives channel switches
  // because the provider now lives at the App root.
  const callStartedRef = useRef<{
    channelId: string;
    userId: string;
  } | null>(null);

  // Synchronous double-fire guard. The state-based check on
  // `joiningCallChannelId` doesn't protect against a user clicking two
  // call buttons in the same tick — both invocations run on the SAME
  // closure with the SAME captured state, both see `null`, both
  // proceed to fire concurrent token requests + mutator writes. A ref
  // updates synchronously and is read inside the body, so the second
  // click hits the guard before its effects start.
  const inFlightRef = useRef(false);
  const handoffPendingRef = useRef(false);

  const sendCallEnded = useCallback(() => {
    const ctx = callStartedRef.current;
    if (!ctx) return;
    zero.mutate(
      mutators.actions.sendCallEvent({
        channelId: ctx.channelId,
        userId: ctx.userId,
        eventType: "call_ended",
      }),
    );
    callStartedRef.current = null;
  }, [zero]);

  const openNativeCallWindow = useCallback(
    async (payload: ElectronCallWindowPayload) => {
      if (!isElectronRuntime()) return false;
      const api = (
        window as unknown as {
          __electronApi?: {
            openCallWindow?: (
              payload: ElectronCallWindowPayload,
            ) => Promise<boolean>;
          };
        }
      ).__electronApi;
      if (!api?.openCallWindow) return false;

      handoffPendingRef.current = true;
      try {
        const opened = await api.openCallWindow(payload);
        if (!opened) {
          handoffPendingRef.current = false;
          return false;
        }
        return true;
      } catch {
        handoffPendingRef.current = false;
        return false;
      }
    },
    [],
  );

  const joinCall = useCallback(
    async (args: JoinCallArgs) => {
      if (!CALLS_ENABLED) return;
      // The ref-based guard runs first because state is async — see
      // the `inFlightRef` declaration. The state checks below are
      // belt-and-suspenders for the single-click case.
      if (inFlightRef.current) return;
      if (joiningCallChannelId || callDialogOpen) return;
      inFlightRef.current = true;

      const { channelId, workspaceId, channelName, mode } = args;
      setJoiningCallChannelId(channelId);
      setJoiningCallMode(mode);
      try {
        const accessToken = await getAccessTokenWithRetry(getAccessToken, {
          maxWaitMs: 5000,
          intervalMs: 200,
        });
        if (!accessToken) {
          toast.error("Authentication required to start calls");
          return;
        }

        const callToken: ChannelCallToken = await createChannelCallToken({
          // Pass through; createChannelCallToken omits an empty
          // workspaceId from the body so the server's optional schema
          // skips the workspace check.
          workspaceId: workspaceId ?? undefined,
          channelId,
          mode,
          accessToken,
        });

        if (!args.skipNotification && user?.id) {
          const eventType =
            mode === "video" ? "started_video_call" : "started_audio_call";
          zero.mutate(
            mutators.actions.sendCallEvent({
              channelId,
              userId: user.id,
              eventType,
            }),
          );
          callStartedRef.current = { channelId, userId: user.id };
        }

        const mediaReady = await warmCallMediaPermissions(mode);
        if (!mediaReady) {
          toast.error(
            "Camera/microphone access was blocked. Enable permissions and try again.",
          );
          sendCallEnded();
          return;
        }
        const nextActiveCall: ActiveCall = {
          channelId,
          workspaceId,
          mode,
          serverUrl: callToken.serverUrl,
          token: callToken.token,
          joinUrl: callToken.joinUrl,
          channelName: channelName.trim() || "channel",
        };
        const openedNativeWindow = await openNativeCallWindow(nextActiveCall);
        setActiveCall(nextActiveCall);
        if (openedNativeWindow) {
          setNativeCallWindowOpen(true);
          setCallDialogOpen(false);
        } else {
          setCallDialogOpen(true);
        }
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to join call",
        );
        // Same ghost-event prevention as the media-blocked path: if
        // we threw AFTER the `started_*_call` broadcast (e.g. media
        // warm-up rejected with an unexpected error, post-mutator
        // network blip), match it with `call_ended` so the indicator
        // clears.
        sendCallEnded();
      } finally {
        inFlightRef.current = false;
        setJoiningCallChannelId(null);
        setJoiningCallMode(null);
      }
    },
    [
      callDialogOpen,
      getAccessToken,
      joiningCallChannelId,
      openNativeCallWindow,
      sendCallEnded,
      user?.id,
      zero,
    ],
  );

  const popOutCall = useCallback(async () => {
    if (!activeCall || !isElectronRuntime()) return false;
    const opened = await openNativeCallWindow(activeCall);
    if (opened) {
      setNativeCallWindowOpen(true);
      setCallDialogOpen(false);
      return true;
    }
    toast.error("Couldn't open the detached call window.");
    return false;
  }, [activeCall, openNativeCallWindow]);

  const closeNativeCallWindow = useCallback(async () => {
    const api = (
      window as unknown as {
        __electronApi?: {
          closeCallWindow?: () => Promise<boolean>;
        };
      }
    ).__electronApi;
    if (!api?.closeCallWindow) return false;
    return api.closeCallWindow();
  }, []);

  useEffect(() => {
    return () => {
      sendCallEnded();
      if (!isElectronRuntime()) return;
      const api = (
        window as unknown as {
          __electronApi?: {
            closeCallWindow?: () => Promise<boolean>;
          };
        }
      ).__electronApi;
      void api?.closeCallWindow?.();
    };
  }, [sendCallEnded]);

  const handleInlineDisconnected = useCallback(() => {
    if (handoffPendingRef.current) {
      handoffPendingRef.current = false;
      return;
    }
    setNativeCallWindowOpen(false);
    setCallDialogOpen(false);
    setActiveCall(null);
    sendCallEnded();
  }, [sendCallEnded]);

  const endCall = useCallback(() => {
    setCallDialogOpen(false);
    setNativeCallWindowOpen(false);
    void closeNativeCallWindow();
  }, [closeNativeCallWindow]);

  useEffect(() => {
    if (!isElectronRuntime()) return;
    const api = (
      window as unknown as {
        __electronApi?: {
          onCallWindowClosed?: (callback: () => void) => () => void;
        };
      }
    ).__electronApi;
    if (!api?.onCallWindowClosed) return;
    return api.onCallWindowClosed(() => {
      handoffPendingRef.current = false;
      setNativeCallWindowOpen(false);
      setCallDialogOpen(false);
      setActiveCall(null);
      sendCallEnded();
    });
  }, [sendCallEnded]);

  // Cleanup effect — fires when the dialog closes (manual leave,
  // SDK-driven disconnect, error path). Sends `call_ended` exactly once
  // per started call (callStartedRef is cleared inside sendCallEnded).
  useEffect(() => {
    if (callDialogOpen) return;
    if (handoffPendingRef.current || nativeCallWindowOpen) return;
    sendCallEnded();
    setActiveCall(null);
  }, [callDialogOpen, nativeCallWindowOpen, sendCallEnded]);

  const isCallActionDisabled =
    joiningCallChannelId !== null || callDialogOpen || nativeCallWindowOpen;

  const value = useMemo<CallContextValue>(
    () => ({
      callsEnabled: CALLS_ENABLED,
      joiningCallChannelId,
      joiningCallMode,
      isCallActionDisabled,
      joinCall,
      endCall,
      handleInlineDisconnected,
      activeCall,
      callDialogOpen,
      nativeCallWindowOpen,
      popOutCall,
      closeNativeCallWindow,
    }),
    [
      joiningCallChannelId,
      joiningCallMode,
      isCallActionDisabled,
      joinCall,
      endCall,
      handleInlineDisconnected,
      activeCall,
      callDialogOpen,
      nativeCallWindowOpen,
      popOutCall,
      closeNativeCallWindow,
    ],
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCall(): CallContextValue {
  const ctx = useContext(CallContext);
  if (!ctx) {
    throw new Error("useCall must be called inside <CallProvider>");
  }
  return ctx;
}
